import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';

/**
 * XP calculation constants
 */
export const XP_CONSTANTS = {
    CORRECT_ANSWER: 10,
    INCORRECT_ANSWER: 2,
    QUIZ_COMPLETION: 5,
    DAILY_STREAK: 5
};

/**
 * Updates user's XP based on quiz performance
 * @param {string} userId - The user's ID
 * @param {Object} quizResults - Quiz performance data
 * @param {number} quizResults.correctAnswers - Number of correct answers
 * @param {number} quizResults.incorrectAnswers - Number of incorrect answers
 * @param {boolean} quizResults.completed - Whether the quiz was completed
 * @returns {Promise<number>} - The updated total XP
 */
export const updateUserXP = async (userId, quizResults) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
            // Create user document if it doesn't exist
            await setDoc(userRef, {
                totalXP: 0,
                streak: 0,
                lastActivityDate: serverTimestamp()
            });
        }

        const userData = userDoc.exists() ? userDoc.data() : {};
        const now = new Date();
        const lastActivityDate = userData.lastActivityDate?.toDate() || new Date(0);
        const isConsecutiveDay = lastActivityDate.toDateString() === new Date(now.setDate(now.getDate() - 1)).toDateString();

        // Calculate XP
        let xpEarned = 0;
        xpEarned += quizResults.correctAnswers * XP_CONSTANTS.CORRECT_ANSWER;
        xpEarned += quizResults.incorrectAnswers * XP_CONSTANTS.INCORRECT_ANSWER;
        if (quizResults.completed) {
            xpEarned += XP_CONSTANTS.QUIZ_COMPLETION;
        }
        if (isConsecutiveDay) {
            xpEarned += XP_CONSTANTS.DAILY_STREAK;
        }

        // Update user document
        await updateDoc(userRef, {
            totalXP: increment(xpEarned),
            lastActivityDate: serverTimestamp(),
            streak: isConsecutiveDay ? increment(1) : 1
        });

        return xpEarned;
    } catch (error) {
        console.error('Error updating XP:', error);
        throw error;
    }
};

/**
 * Gets user's current XP and streak
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - User's XP data
 */
export const getUserXPData = async (userId) => {
    try {
        console.log('Fetching XP data for user:', userId);
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
            console.log('User document not found, creating new document');
            // Create user document if it doesn't exist
            await setDoc(userRef, {
                totalXP: 0,
                streak: 0,
                lastActivityDate: serverTimestamp()
            });
            return {
                totalXP: 0,
                streak: 0,
                lastActivityDate: null
            };
        }

        const userData = userDoc.data();
        console.log('Retrieved user data:', userData);
        
        return {
            totalXP: userData.totalXP || 0,
            streak: userData.streak || 0,
            lastActivityDate: userData.lastActivityDate?.toDate() || null
        };
    } catch (error) {
        console.error('Error fetching XP data:', error);
        return {
            totalXP: 0,
            streak: 0,
            lastActivityDate: null
        };
    }
}; 